<!-- TODO: refactor to MapLayer and MapSource vue components -->
<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watchEffect, watch} from 'vue'
import {useStore} from 'vuex'
import {useRoute, useRouter} from 'vue-router'
import { v4 as uuidv4 } from 'uuid'
import {bbox} from '@turf/turf'

import {Trash2, Eye, Upload, Edit2, FolderOpen} from 'lucide-vue-next'
import {useToast} from '@/components/ui/toast/use-toast'
import {Button} from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ProjectSelect } from '@/components/ui/project-select'
import { BulkUploadTable, BulkEditDialog, UploadProgressDialog } from '@/components/bulk-upload'
import { HOST, API } from '@/utils/http'

import {parseKML, parseKMZ, parseGeoJSON, parseImage, parseGPX, parseFIT, parseShapefileZip, parseShapefileBundle} from '@/composables/files'
import { uploadResource } from '@/services/resourcesApi'
import ProjWarningDialog from './ProjWarningDialog.vue'
import FileMetadataDialog from './FileMetadataDialog.vue'

const SHAPEFILE_EXTS = new Set(['shp', 'shx', 'dbf', 'prj', 'cpg', 'sbn', 'sbx', 'qix'])

// Internal keys filtered out when listing user-facing attribute columns.
const INTERNAL_PROP_KEYS = new Set([
  'local_uuid', 'fileName', 'isEditing',
  '_parentUuid', '_splitIndex', '_autoCombined',
  'needsLocation', 'resource'
])

// Ordered so adjacent entries come from different hue families — index-order
// assignment is enough for visual separation between neighbouring groups.
const GROUP_COLOR_PALETTE = [
  '#ef4444', // red
  '#10b981', // emerald
  '#3b82f6', // blue
  '#f97316', // orange
  '#8b5cf6', // violet
  '#14b8a6', // teal
  '#ec4899', // pink
  '#eab308', // yellow
  '#0ea5e9', // sky
  '#84cc16', // lime
  '#a855f7', // purple
  '#06b6d4', // cyan
  '#d946ef', // fuchsia
  '#f59e0b', // amber
  '#22c55e', // green
  '#64748b'  // slate
]
const UNGROUPED_COLOR = '#94a3b8'
const UNGROUPED_PREFIX = '(no '

function hashKey(s) {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i)
    h |= 0
  }
  return Math.abs(h)
}

function colorForGroupIndex(index) {
  const n = GROUP_COLOR_PALETTE.length
  return GROUP_COLOR_PALETTE[index % n]
}

function colorForGroupKey(key) {
  if (typeof key === 'string' && key.startsWith(UNGROUPED_PREFIX)) return UNGROUPED_COLOR
  return GROUP_COLOR_PALETTE[hashKey(String(key)) % GROUP_COLOR_PALETTE.length]
}

// Returns the common color if every feature carries the same non-empty
// `color`, else null — lets pre-styled inputs keep their palette.
function sharedFeatureColor(features) {
  if (!features?.length) return null
  let shared = null
  for (const f of features) {
    const c = f.properties?.color
    if (typeof c !== 'string' || c.trim() === '') return null
    if (shared === null) shared = c
    else if (shared !== c) return null
  }
  return shared
}

// Color precedence: explicit feature.color > shared color inferred from group
// members > deterministic hash color as a last resort.
function resolveGroupColor(features, fallbackKey) {
  return sharedFeatureColor(features) || colorForGroupKey(fallbackKey)
}

function availableNameColumns(features) {
  const keys = new Set()
  for (const f of features) {
    if (!f.properties) continue
    for (const [k, v] of Object.entries(f.properties)) {
      if (INTERNAL_PROP_KEYS.has(k)) continue
      if (k === 'name' || k === 'description' || k === 'color') continue
      if (typeof v === 'string' && v.trim() !== '') keys.add(k)
      else if (typeof v === 'number') keys.add(k)
    }
  }
  return Array.from(keys)
}

const FILE_INPUT_ACCEPT = [
  '.kml', '.kmz', '.geojson', '.gpx', '.fit', '.zip',
  '.shp', '.shx', '.dbf', '.prj', '.cpg',
  '.jpg', '.jpeg', '.png', '.heic', '.heif', '.avif', '.webp'
].join(',')

const { toast } = useToast()
const route = useRoute()
const router = useRouter()
const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const loadedFiles = computed(() => store.getters['geo/getFiles'])
const orgProjects = ref([])
const projects = computed(() => orgProjects.value)
const currentProject = computed(() => store.getters['project/getProject'])
const selectedOrgId = computed(() => route.params.org_id)

const MAX_FILES = 30

const isUploading = ref(false)
const selectedFeatures = ref([])
const showBulkEditDialog = ref(false)
const showUploadProgressDialog = ref(false)
const uploadProgressRef = ref(null)
// URL is the source of truth — picks navigate via switchProject.
const selectedProjectId = computed(() => {
  const raw = route.params.project_id
  if (!raw) return null
  const n = parseInt(raw, 10)
  return Number.isFinite(n) ? n : null
})
const requireProjectSelection = computed(() => !selectedProjectId.value)
const uploadCancelled = ref(false)
const fileInputRef = ref(null)
const emptyStateDragging = ref(false)

const onFilePicked = async (e) => {
  const files = e.target.files
  if (files && files.length) {
    await handleFiles(files)
  }
  e.target.value = ''
}

const onEmptyStateDrop = async (e) => {
  emptyStateDragging.value = false
  const files = e.dataTransfer?.files
  if (files && files.length) {
    await handleFiles(files)
  }
}

const projWarning = ref({ open: false, fileName: '' })
let projWarningResolver = null
const confirmNoPrj = (fileName) => {
  projWarning.value = { open: true, fileName }
  return new Promise((resolve) => { projWarningResolver = resolve })
}
const onProjWarningConfirm = () => {
  projWarning.value = { open: false, fileName: '' }
  projWarningResolver?.(true)
  projWarningResolver = null
}
const onProjWarningCancel = () => {
  projWarning.value = { open: false, fileName: '' }
  projWarningResolver?.(false)
  projWarningResolver = null
}

const metadataDialog = ref({ open: false, fileName: '', singleFeature: null })
const openMetadataDialog = (fileName) => {
  metadataDialog.value = { open: true, fileName, singleFeature: null }
}
const openFeatureMetadataDialog = (feature) => {
  const fileName = feature.properties?.fileName || 'Feature'
  const displayName = feature.properties?.name ? ` — ${feature.properties.name}` : ''
  metadataDialog.value = {
    open: true,
    fileName: `${fileName}${displayName}`,
    singleFeature: feature
  }
}
const metadataDialogFeatures = computed(() => {
  if (!metadataDialog.value.open) return []
  if (metadataDialog.value.singleFeature) return [metadataDialog.value.singleFeature]
  if (!metadataDialog.value.fileName) return []
  return collection.value.features.filter(
    f => f.properties?.fileName === metadataDialog.value.fileName
  )
})

const collection = ref({
  type: 'FeatureCollection',
  features: []
})

const fileVisibility = ref({})
const fileGroupPolygons = ref({})
const fileGroupColumns = ref({})
const fileValueVisibility = ref({})
// Keyed by `${fileName}${KEY_SEP}${value}` (or `${GLOBAL_PREFIX}${KEY_SEP}${value}`), with either:
//   { kind: 'existing', templateId: <number> }  or  { kind: 'new', name: <string> }
const valueGroupTemplates = ref({})

const GLOBAL_PREFIX = '__global__'
// Unit Separator — safe inside filenames and GeoJSON attribute values, so it won't
// collide with user data the way `|` would.
const KEY_SEP = '\x1F'

const globalGroupColumn = ref(null)

const globalAvailableColumns = computed(() => {
  const byFile = {}
  for (const feature of effectiveFeatures.value) {
    const fn = feature.properties?.fileName || 'Unknown'
    if (!byFile[fn]) byFile[fn] = []
    byFile[fn].push(feature)
  }
  const fileSets = Object.values(byFile).map(features => new Set(availableNameColumns(features)))
  if (fileSets.length === 0) return []
  let intersection = fileSets[0]
  for (let i = 1; i < fileSets.length; i++) {
    intersection = new Set([...intersection].filter(k => fileSets[i].has(k)))
  }
  return Array.from(intersection)
})

// Org-wide endpoint returns templates from every project, so filter down to the
// active one. In scope: project_ids includes activeId, OR legacy project_id ===
// activeId, OR both unbound (org-level shared template).
const allTemplates = computed(() => {
  const org = store.getters['geo/getAllTemplates'] || []
  const project = store.getters['geo/getProjectReportTemplates'] || []
  const activeId = selectedProjectId.value
  const seen = new Set()
  const merged = []
  for (const t of [...project, ...org]) {
    if (!t?.id || seen.has(t.id)) continue
    seen.add(t.id)

    if (activeId == null) {
      merged.push(t)
      continue
    }

    const pids = Array.isArray(t.project_ids) ? t.project_ids : null
    const pid = t.project_id ?? null
    const matchesMany = pids && pids.length ? pids.includes(activeId) : false
    const matchesOne = pid != null ? pid === activeId : false
    const unbound = (pid == null) && (!pids || pids.length === 0)
    if (matchesMany || matchesOne || unbound) merged.push(t)
  }
  return merged
})

const setValueGroupTemplate = (fileName, value, choice) => {
  const key = `${fileName}${KEY_SEP}${value}`
  if (!choice) delete valueGroupTemplates.value[key]
  else valueGroupTemplates.value[key] = choice
}

const setGlobalGroupColumn = (column) => {
  globalGroupColumn.value = column
  for (const key of Object.keys(fileValueVisibility.value)) {
    if (key.startsWith(`${GLOBAL_PREFIX}${KEY_SEP}`)) delete fileValueVisibility.value[key]
  }
  for (const key of Object.keys(valueGroupTemplates.value)) {
    if (key.startsWith(`${GLOBAL_PREFIX}${KEY_SEP}`)) delete valueGroupTemplates.value[key]
  }
  if (column) {
    for (const key of Object.keys(fileGroupColumns.value)) {
      fileGroupColumns.value[key] = null
    }
    const templatesByName = new Map()
    for (const t of allTemplates.value) {
      const k = (t?.name || '').trim().toLowerCase()
      if (k && !templatesByName.has(k)) templatesByName.set(k, t)
    }
    const seen = new Set()
    for (const f of effectiveFeatures.value) {
      const v = bucketKeyForValue(f.properties?.[column], column)
      if (v.startsWith(UNGROUPED_PREFIX)) continue
      if (seen.has(v)) continue
      seen.add(v)
      const match = templatesByName.get(v.trim().toLowerCase())
      valueGroupTemplates.value[`${GLOBAL_PREFIX}${KEY_SEP}${v}`] = match
        ? { kind: 'existing', templateId: match.id }
        : { kind: 'new', name: v }
    }
  }
  syncMapSource()
}

const toggleGlobalValueVisibility = (value) => {
  const key = `${GLOBAL_PREFIX}${KEY_SEP}${value}`
  fileValueVisibility.value[key] = !(fileValueVisibility.value[key] !== false)
}

const mergeSelectMode = ref(false)
const mergeSelectedFileNames = ref(new Set())

// When groupPolygons is off for a file, MultiPolygons are split into Polygons.
const effectiveFeatures = computed(() => {
  const result = []
  for (const feature of collection.value.features) {
    const fileName = feature.properties?.fileName
    const shouldGroup = fileGroupPolygons.value[fileName] !== false

    if (!shouldGroup && feature.geometry?.type === 'MultiPolygon') {
      for (let i = 0; i < feature.geometry.coordinates.length; i++) {
        result.push({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: feature.geometry.coordinates[i]
          },
          properties: {
            ...feature.properties,
            name: `${feature.properties?.name || 'Unnamed'} (${i + 1})`,
            local_uuid: `${feature.properties.local_uuid}_split_${i}`,
            _parentUuid: feature.properties.local_uuid,
            _splitIndex: i,
            isEditing: false
          }
        })
      }
    } else {
      result.push(feature)
    }
  }
  return result
})

function bucketKeyForValue(raw, propKey) {
  return (raw == null || (typeof raw === 'string' && raw.trim() === ''))
    ? `${UNGROUPED_PREFIX}${propKey})`
    : String(raw)
}

function buildValueGroups(fileName, features) {
  const col = fileGroupColumns.value[fileName]
  if (!col) return []

  const buckets = new Map()
  for (const f of features) {
    const key = bucketKeyForValue(f.properties?.[col], col)
    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key).push(f)
  }

  const entries = Array.from(buckets.entries()).map(([value, valueFeatures], idx) => {
    const tplKey = `${fileName}${KEY_SEP}${value}`
    const geomType = (valueFeatures[0]?.geometry?.type || '').replace(/^Multi/, '')
    // Index-based so adjacent buckets get distinct hues; hash-based collides often at 7–10 buckets.
    const isUngrouped = value.startsWith(UNGROUPED_PREFIX)
    const paletteColor = isUngrouped ? UNGROUPED_COLOR : colorForGroupIndex(idx)
    const color = sharedFeatureColor(valueFeatures) || paletteColor
    return {
      value,
      features: valueFeatures,
      visible: fileValueVisibility.value[tplKey] !== false,
      color,
      templateChoice: valueGroupTemplates.value[tplKey] || null,
      geomType
    }
  })
  // Ungrouped bucket always last
  entries.sort((a, b) => {
    const aU = a.value.startsWith(UNGROUPED_PREFIX) ? 1 : 0
    const bU = b.value.startsWith(UNGROUPED_PREFIX) ? 1 : 0
    return aU - bU
  })
  return entries
}

// Groups by fileName, or by globalGroupColumn value when global grouping is on.
const fileGroups = computed(() => {
  if (globalGroupColumn.value) {
    const col = globalGroupColumn.value
    const buckets = new Map()
    for (const f of effectiveFeatures.value) {
      const key = bucketKeyForValue(f.properties?.[col], col)
      if (!buckets.has(key)) buckets.set(key, [])
      buckets.get(key).push(f)
    }
    const entries = Array.from(buckets.entries()).map(([value, features], idx) => {
      const tplKey = `${GLOBAL_PREFIX}${KEY_SEP}${value}`
      const isUngrouped = value.startsWith(UNGROUPED_PREFIX)
      const paletteColor = isUngrouped ? UNGROUPED_COLOR : colorForGroupIndex(idx)
      const color = sharedFeatureColor(features) || paletteColor
      const geomType = (features[0]?.geometry?.type || '').replace(/^Multi/, '')
      return {
        fileName: value,
        features,
        visible: fileValueVisibility.value[tplKey] !== false,
        groupPolygons: true,
        availableColumns: [],
        groupColumn: null,
        valueGroups: [],
        color,
        isMerged: false,
        mergeSelected: false,
        isGlobalGroup: true,
        globalValue: value,
        templateChoice: valueGroupTemplates.value[tplKey] || null,
        geomType
      }
    })
    entries.sort((a, b) => {
      const aU = a.globalValue.startsWith(UNGROUPED_PREFIX) ? 1 : 0
      const bU = b.globalValue.startsWith(UNGROUPED_PREFIX) ? 1 : 0
      return aU - bU
    })
    return entries
  }

  const groups = {}
  for (const feature of effectiveFeatures.value) {
    const key = feature.properties?.fileName || 'Unknown'
    if (!groups[key]) groups[key] = []
    groups[key].push(feature)
  }

  return Object.entries(groups).map(([fileName, features], idx) => {
    const groupColumn = fileGroupColumns.value[fileName] || null
    const isMerged = features.some(f => f.properties?._originalFileName)
    const paletteColor = colorForGroupIndex(idx)
    const color = sharedFeatureColor(features) || paletteColor
    return {
      fileName,
      features,
      visible: fileVisibility.value[fileName] !== false,
      groupPolygons: fileGroupPolygons.value[fileName] !== false,
      availableColumns: availableNameColumns(features),
      groupColumn,
      valueGroups: buildValueGroups(fileName, features),
      color,
      isMerged,
      mergeSelected: mergeSelectedFileNames.value.has(fileName),
      isGlobalGroup: false
    }
  })
})

// Map features must inherit the exact same hues as the list-side swatches.
const groupColorLookup = computed(() => {
  const map = new Map()
  for (const g of fileGroups.value) {
    if (g.isGlobalGroup) {
      map.set(`val|${GLOBAL_PREFIX}${KEY_SEP}${g.globalValue}`, g.color)
    } else {
      map.set(`file|${g.fileName}`, g.color)
      for (const vg of g.valueGroups || []) {
        map.set(`val|${g.fileName}${KEY_SEP}${vg.value}`, vg.color)
      }
    }
  }
  return map
})

const visibleMapFeatures = computed(() => {
  const lookup = groupColorLookup.value
  const result = []
  const isGlobal = !!globalGroupColumn.value
  const globalCol = globalGroupColumn.value
  for (const f of effectiveFeatures.value) {
    if (f.properties?.isEditing) continue
    const fileName = f.properties?.fileName || 'Unknown'

    let color
    if (isGlobal) {
      const valueKey = bucketKeyForValue(f.properties?.[globalCol], globalCol)
      const tplKey = `${GLOBAL_PREFIX}${KEY_SEP}${valueKey}`
      if (fileValueVisibility.value[tplKey] === false) continue
      color = lookup.get(`val|${GLOBAL_PREFIX}${KEY_SEP}${valueKey}`)
    } else {
      if (fileVisibility.value[fileName] === false) continue
      const col = fileGroupColumns.value[fileName]
      if (col) {
        const valueKey = bucketKeyForValue(f.properties?.[col], col)
        if (fileValueVisibility.value[`${fileName}${KEY_SEP}${valueKey}`] === false) continue
        color = lookup.get(`val|${fileName}${KEY_SEP}${valueKey}`)
      } else {
        color = lookup.get(`file|${fileName}`)
      }
    }
    const existing = f.properties?.color
    const finalColor = (typeof existing === 'string' && existing.trim() !== '')
      ? existing
      : (color || colorForGroupIndex(0))
    result.push({
      ...f,
      properties: { ...f.properties, color: finalColor }
    })
  }
  return result
})

const syncMapSource = () => {
  if (!map.value?.getSource('temp_data')) return
  map.value.getSource('temp_data').setData({
    type: 'FeatureCollection',
    features: visibleMapFeatures.value
  })
}

watch(globalAvailableColumns, (cols) => {
  if (globalGroupColumn.value && !cols.includes(globalGroupColumn.value)) {
    globalGroupColumn.value = null
    toast({ title: 'Global grouping reset — column no longer available across all files' })
  }
})

watch(visibleMapFeatures, () => {
  syncMapSource()
}, { deep: true })

const toggleFileVisibility = (fileName) => {
  fileVisibility.value[fileName] = !(fileVisibility.value[fileName] !== false)
}

const toggleValueGroupVisibility = (fileName, value) => {
  const key = `${fileName}${KEY_SEP}${value}`
  fileValueVisibility.value[key] = !(fileValueVisibility.value[key] !== false)
}

const setFileGroupColumn = (fileName, column) => {
  fileGroupColumns.value[fileName] = column
  // Reset nested state — keyed on the old column's value buckets.
  for (const key of Object.keys(fileValueVisibility.value)) {
    if (key.startsWith(`${fileName}${KEY_SEP}`)) delete fileValueVisibility.value[key]
  }
  for (const key of Object.keys(valueGroupTemplates.value)) {
    if (key.startsWith(`${fileName}${KEY_SEP}`)) delete valueGroupTemplates.value[key]
  }
  // Default each populated bucket to an existing template when names match
  // (case-insensitive, trimmed), otherwise mark it for creation.
  if (column) {
    const templatesByName = new Map()
    for (const t of allTemplates.value) {
      const key = (t?.name || '').trim().toLowerCase()
      if (key && !templatesByName.has(key)) templatesByName.set(key, t)
    }
    const seen = new Set()
    for (const f of collection.value.features) {
      if (f.properties?.fileName !== fileName) continue
      const v = bucketKeyForValue(f.properties?.[column], column)
      if (v.startsWith(UNGROUPED_PREFIX)) continue
      if (seen.has(v)) continue
      seen.add(v)
      const match = templatesByName.get(v.trim().toLowerCase())
      valueGroupTemplates.value[`${fileName}${KEY_SEP}${v}`] = match
        ? { kind: 'existing', templateId: match.id }
        : { kind: 'new', name: v }
    }
  }
  syncMapSource()
}

const toggleMergeSelectMode = () => {
  mergeSelectMode.value = !mergeSelectMode.value
  mergeSelectedFileNames.value = new Set()
}

const toggleMergeSelection = (fileName) => {
  const next = new Set(mergeSelectedFileNames.value)
  if (next.has(fileName)) next.delete(fileName)
  else next.add(fileName)
  mergeSelectedFileNames.value = next
}

const confirmMergeFiles = () => {
  const selected = Array.from(mergeSelectedFileNames.value)
  if (selected.length < 2) {
    toast({ title: 'Select at least 2 files to merge', variant: 'destructive' })
    return
  }
  const stem = (n) => n?.includes('.') ? n.slice(0, n.lastIndexOf('.')) : n
  const newName = selected.map(stem).join(' + ')

  for (const f of collection.value.features) {
    const fn = f.properties?.fileName
    if (!fn || !mergeSelectedFileNames.value.has(fn)) continue
    if (!f.properties._originalFileName) f.properties._originalFileName = fn
    f.properties.fileName = newName
  }

  const first = selected[0]
  fileVisibility.value[newName] = fileVisibility.value[first] !== false
  fileGroupPolygons.value[newName] = fileGroupPolygons.value[first] !== false
  fileGroupColumns.value[newName] = null
  for (const name of selected) {
    if (name === newName) continue
    delete fileVisibility.value[name]
    delete fileGroupPolygons.value[name]
    delete fileGroupColumns.value[name]
    for (const k of Object.keys(fileValueVisibility.value)) {
      if (k.startsWith(`${name}${KEY_SEP}`)) delete fileValueVisibility.value[k]
    }
  }

  mergeSelectMode.value = false
  mergeSelectedFileNames.value = new Set()
  syncMapSource()
  toast({ title: `Merged ${selected.length} files`, description: newName })
}

const splitMergedFile = (fileName) => {
  const restored = new Set()
  for (const f of collection.value.features) {
    if (f.properties?.fileName !== fileName) continue
    const original = f.properties._originalFileName
    if (!original) continue
    f.properties.fileName = original
    delete f.properties._originalFileName
    restored.add(original)
  }
  if (!restored.size) {
    toast({ title: 'Nothing to split — this file has no merged sources' })
    return
  }
  const mergedVisible = fileVisibility.value[fileName] !== false
  for (const orig of restored) {
    if (!(orig in fileVisibility.value)) fileVisibility.value[orig] = mergedVisible
    if (!(orig in fileGroupPolygons.value)) fileGroupPolygons.value[orig] = true
  }
  delete fileVisibility.value[fileName]
  delete fileGroupPolygons.value[fileName]
  delete fileGroupColumns.value[fileName]
  for (const k of Object.keys(fileValueVisibility.value)) {
    if (k.startsWith(`${fileName}${KEY_SEP}`)) delete fileValueVisibility.value[k]
  }
  syncMapSource()
  toast({ title: `Split back to ${restored.size} files` })
}

const toggleFileGroupPolygons = (fileName) => {
  fileGroupPolygons.value[fileName] = !(fileGroupPolygons.value[fileName] !== false)
}


const activeLayers = []
const activeSources = []

const setCollection = (fileCollection, fileName) => {
  createLayers()

  if (!(fileName in fileVisibility.value)) {
    fileVisibility.value[fileName] = true
  }
  if (!(fileName in fileGroupPolygons.value)) {
    fileGroupPolygons.value[fileName] = true
  }

  for (let feature of fileCollection.features) {
    // Fallback geometry for images with no GPS data.
    if (!feature.geometry && feature.properties?.needsLocation && map.value) {
      const center = map.value.getCenter()
      feature.geometry = { type: 'Point', coordinates: [center.lng, center.lat] }
    }

    feature.properties = {
      ...feature.properties,
      fileName: fileName,
      name: feature.properties?.name || feature.properties?.Name || fileName.split('.').shift(),
      description: '',
      local_uuid: uuidv4(),
      isEditing: false
    }
    collection.value.features.push(feature)
  }

  // Auto-combine into MultiPolygon above 5 Polygons so the UI doesn't list each one.
  const polygonsFromFile = collection.value.features.filter(
    f => f.properties?.fileName === fileName && f.geometry?.type === 'Polygon'
  )
  if (polygonsFromFile.length > 5) {
    const multiPolygon = {
      type: 'Feature',
      geometry: {
        type: 'MultiPolygon',
        coordinates: polygonsFromFile.map(f => f.geometry.coordinates)
      },
      properties: {
        ...polygonsFromFile[0].properties,
        name: polygonsFromFile[0].properties?.name || fileName.split('.').shift(),
        local_uuid: uuidv4(),
        _autoCombined: true,
        isEditing: false
      }
    }

    const polygonUuids = new Set(polygonsFromFile.map(f => f.properties.local_uuid))
    collection.value.features = collection.value.features.filter(
      f => !polygonUuids.has(f.properties?.local_uuid)
    )
    collection.value.features.push(multiPolygon)
  }

  syncMapSource()
}

const splitFeature = (feature) => {
  collection.value.features = collection.value.features.filter(f => f.properties.local_uuid !== feature.properties.local_uuid)

  if (feature.geometry?.type === 'GeometryCollection') {
    for (let geometry of feature.geometry.geometries) {
      const geoFeature = {
        type: 'Feature',
        geometry: geometry,
        properties: {
          ...feature.properties ? feature.properties : {},
          name: feature.properties.name || feature.properties.fileName?.split('.').shift() || 'Unnamed',
          description: feature.properties.description || '',
          fileName: feature.properties.fileName,
          local_uuid: uuidv4(),
          isEditing: false
        }
      }
      collection.value.features.push(geoFeature)
    }
  }
}

const applyShapefileResult = async (result, bundleName) => {
  if (!result || !result.layers?.length) return
  if (result.warn === 'missing-prj') {
    const proceed = await confirmNoPrj(bundleName)
    if (!proceed) {
      toast({ title: `Skipped ${bundleName}`, description: 'No .prj file and coordinates looked projected.' })
      return
    }
  }
  for (const layer of result.layers) {
    setCollection(layer.collection, layer.name)
  }
}

const handleFile = async (file) => {
  const ext = file.name.split('.').pop().toLowerCase()

  switch (ext) {
    case 'kml': {
      const r = await parseKML(file)
      if (r) setCollection(r.collection, r.name)
      break
    }
    case 'kmz': {
      const r = await parseKMZ(file)
      if (r) setCollection(r.collection, r.name)
      break
    }
    case 'geojson': {
      const r = await parseGeoJSON(file)
      if (r) setCollection(r.collection, r.name)
      break
    }
    case 'gpx': {
      const r = await parseGPX(file)
      if (r) setCollection(r.collection, r.name)
      break
    }
    case 'fit': {
      const r = await parseFIT(file)
      if (r) setCollection(r.collection, r.name)
      break
    }
    case 'zip': {
      const r = await parseShapefileZip(file)
      await applyShapefileResult(r, file.name)
      break
    }
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'heic':
    case 'heif':
    case 'avif':
    case 'webp': {
      const r = await parseImage(file)
      if (r) setCollection(r.collection, r.name)
      break
    }
    default:
      toast({ title: 'Invalid file type', status: 'error' })
      break
  }
}

const partitionFiles = (files) => {
  const bundles = new Map()
  const others = []

  for (const file of files) {
    const dotIdx = file.name.lastIndexOf('.')
    const ext = dotIdx >= 0 ? file.name.slice(dotIdx + 1).toLowerCase() : ''
    const stem = dotIdx >= 0 ? file.name.slice(0, dotIdx) : file.name

    if (SHAPEFILE_EXTS.has(ext) && ext !== 'zip') {
      if (!bundles.has(stem)) {
        bundles.set(stem, { fileName: `${stem}.shp` })
      }
      bundles.get(stem)[ext] = file
    } else {
      others.push(file)
    }
  }

  return { bundles, others }
}

const handleShapefileBundle = async (bundle) => {
  if (!bundle.shp) {
    toast({ title: `Missing .shp for ${bundle.fileName}`, variant: 'destructive' })
    return
  }
  if (!bundle.shx) {
    toast({ title: `Missing .shx sidecar for ${bundle.fileName}`, variant: 'destructive' })
    return
  }
  if (!bundle.dbf) {
    toast({ title: `No .dbf for ${bundle.fileName} — attributes will be empty` })
  }
  const result = await parseShapefileBundle(bundle)
  await applyShapefileResult(result, bundle.fileName)
}

const handleFiles = async (files) => {
  if (!files) return
  if (files.length === 0) return

  const { bundles, others } = partitionFiles(files)
  const unitCount = bundles.size + others.length

  if (unitCount > MAX_FILES) {
    toast({title: `Only ${MAX_FILES} files can be uploaded at once`})
    return
  }

  let processed = 0
  for (const bundle of bundles.values()) {
    if (processed++ >= MAX_FILES) break
    await handleShapefileBundle(bundle)
  }
  for (const file of others) {
    if (processed++ >= MAX_FILES) break
    await handleFile(file)
  }
}

const createLayers = () => {
  if (!map.value.getSource('temp_data')) {
    map.value.addSource('temp_data', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      },
    });
    activeLayers.push('temp_data');
  }

  if (!map.value.getLayer('temp-geometry-layer-fill')) {
    map.value.addLayer({
      'id': 'temp-geometry-layer-fill',
      'type': 'line',
      'source': 'temp_data',
      'paint': {
        'line-color': ['case', ['has', 'color'], ['get', 'color'], '#0059ff'],
        'line-width': 4
      },
      'filter': ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']]
    });
    activeLayers.push('temp-geometry-layer-fill');
  }

  if (!map.value.getLayer('temp-geometry-layer-lines')) {
    map.value.addLayer({
      'id': 'temp-geometry-layer-lines',
      'type': 'fill',
      'source': 'temp_data',
      'paint': {
        'fill-color': ['case', ['has', 'color'], ['get', 'color'], '#0059ff'],
        'fill-opacity': 0.5
      },
      'filter': ['==', '$type', 'Polygon']
    });
    activeLayers.push('temp-geometry-layer-lines');
  }

  if (!map.value.getLayer('temp-geometry-layer-points')) {
    map.value.addLayer({
      id: 'temp-geometry-layer-points',
      type: 'circle',
      source: 'temp_data',
      paint: {
        'circle-color': ['case', ['has', 'color'], ['get', 'color'], '#0059ff'],
        'circle-radius': 8,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff'
      },
      'filter': ['==', '$type', 'Point']
    });
    activeLayers.push('temp-geometry-layer-points');
  }

  if (!map.value.getSource('temp_data')) return
  syncMapSource()
  activeSources.push('temp_data')
}

const focusCollection = () => {
  const bounds = bbox(collection.value)
  map.value.fitBounds(bounds, { padding: 20 })
}

const removeFeature = async (feature) => {
  // Split child: splice the polygon out of the parent MultiPolygon instead.
  if (feature.properties?._parentUuid) {
    const parentUuid = feature.properties._parentUuid
    const splitIndex = feature.properties._splitIndex
    const parentFeature = collection.value.features.find(
      f => f.properties?.local_uuid === parentUuid
    )
    if (
      parentFeature &&
      typeof splitIndex === 'number' &&
      parentFeature.geometry?.type === 'MultiPolygon' &&
      parentFeature.geometry.coordinates[splitIndex]
    ) {
      parentFeature.geometry.coordinates.splice(splitIndex, 1)
      if (parentFeature.geometry.coordinates.length === 0) {
        collection.value.features = collection.value.features.filter(f => f !== parentFeature)
      }
    }
  } else {
    collection.value.features = collection.value.features.filter(f => f !== feature)
  }
  syncMapSource()

  if (collection.value.features.length === 0) {
    if (feature.properties?.code) {
      await createFeature(feature)
    }
  }
}

const clearCollection = () => {
  collection.value.features = []
  selectedFeatures.value = []
  fileVisibility.value = {}
  fileGroupPolygons.value = {}
  fileGroupColumns.value = {}
  fileValueVisibility.value = {}
  valueGroupTemplates.value = {}
  mergeSelectMode.value = false
  mergeSelectedFileNames.value = new Set()
  syncMapSource()
}

const startEditing = (feature) => {
  for (let f of collection.value.features) {
    if (f.properties.local_uuid === feature.properties.local_uuid) {
      f.properties.isEditing = !f.properties.isEditing
    } else {
      f.properties.isEditing = false
    }
  }
  syncMapSource()
}

const stopEditing = (feature) => {
  for (let f of collection.value.features) {
    if (f.properties.local_uuid === feature.properties.local_uuid) {
      f.geometry = feature.geometry
      f.properties.isEditing = false
    }
  }
  syncMapSource()
}

const updateFeature = (feature) => {
  for (let f of collection.value.features) {
    if (f.properties.local_uuid === feature.properties.local_uuid) {
      f.properties = feature.properties
    }
  }
  syncMapSource()
}

const reset = () => {
  for (let layer of activeLayers) {
    if (map.value.getLayer(layer)) map.value.removeLayer(layer)
  }

  for (let source of activeSources) {
    if (map.value.getSource(source)) map.value.removeSource(source)
  }

  collection.value.features = []
  selectedFeatures.value = []
  fileVisibility.value = {}
  fileGroupPolygons.value = {}
  fileGroupColumns.value = {}
  fileValueVisibility.value = {}
  valueGroupTemplates.value = {}
  mergeSelectMode.value = false
  mergeSelectedFileNames.value = new Set()
  store.commit('geo/SET_FILES', null)
}

const handleSelectFeatures = (features) => {
  selectedFeatures.value = features
}

const handlePreviewFeature = (feature) => {
  const bounds = bbox(feature)
  map.value.fitBounds(bounds, { padding: 20 })
}

const zoomToFile = (fileName) => {
  const group = fileGroups.value.find(g => g.fileName === fileName)
  if (!group || group.features.length === 0) return
  const fc = { type: 'FeatureCollection', features: group.features }
  const bounds = bbox(fc)
  map.value.fitBounds(bounds, { padding: 50 })
}

const handleEditFeature = (feature) => {
  handlePreviewFeature(feature)
  // TODO: Implement individual feature editing
}

const handleDeleteFeature = (feature) => {
  removeFeature(feature)
}

const handleFeatureUploaded = (originalFeature, uploadedFeature) => {
  const index = collection.value.features.findIndex(f =>
    f.properties?.local_uuid === originalFeature.properties?.local_uuid
  )
  if (index !== -1) {
    collection.value.features.splice(index, 1)
  }

  syncMapSource()

  toast({
    title: 'Feature uploaded successfully',
    description: `${uploadedFeature.properties.name} has been uploaded to the project`
  })
}

const handleBulkEdit = () => {
  showBulkEditDialog.value = true
}

const handleBulkEditApply = (changes) => {
  selectedFeatures.value.forEach(feature => {
    if (changes.nameOperation) {
      const currentName = feature.properties.name || feature.properties.fileName || 'Unnamed'

      switch (changes.nameOperation) {
        case 'replace':
          feature.properties.name = changes.nameValue
          break
        case 'prefix':
          feature.properties.name = `${changes.nameValue}${currentName}`
          break
        case 'suffix':
          feature.properties.name = `${currentName}${changes.nameValue}`
          break
      }
    }

    if (changes.descriptionOperation) {
      const currentDescription = feature.properties.description || ''

      switch (changes.descriptionOperation) {
        case 'replace':
          feature.properties.description = changes.descriptionValue
          break
        case 'append':
          feature.properties.description = currentDescription + changes.descriptionValue
          break
      }
    }

    if (changes.colorValue) {
      feature.properties.color = changes.colorValue
    }

    if (changes.visibilityValue !== undefined) {
      feature.properties.shared = changes.visibilityValue
    }

    if (changes.projectValue) {
      feature.properties.project_id = changes.projectValue
    }

    if (changes.staticValue !== undefined) {
      feature.properties.is_static = changes.staticValue
    }
  })

  syncMapSource()

  toast({
    title: `Updated ${selectedFeatures.value.length} features`,
  })
}

const handleUploadSelected = async (features = undefined) => {
  let featuresToProcess = features
  if (!featuresToProcess) {
    featuresToProcess = collection.value.features
  }

  if (featuresToProcess.length === 0) {
    toast({ title: 'No features to upload' })
    return
  }

  const projectId = selectedProjectId.value || currentProject.value?.project_id
  if (!projectId) {
    toast({ title: 'Please select a project first', variant: 'destructive' })
    return
  }

  showUploadProgressDialog.value = true
  isUploading.value = true
  uploadCancelled.value = false

  // Create "new" templates up front so every feature can be tagged before upload.
  const resolvedTemplateIds = {}
  const featuresInUpload = new Set(featuresToProcess.map(f => f.properties?.local_uuid))
  for (const [key, choice] of Object.entries(valueGroupTemplates.value)) {
    const isGlobal = key.startsWith(`${GLOBAL_PREFIX}${KEY_SEP}`)
    const sepIdx = key.indexOf(KEY_SEP)
    if (sepIdx < 0) continue
    const keyValue = key.slice(sepIdx + 1)
    const keyFileName = isGlobal ? null : key.slice(0, sepIdx)
    const col = isGlobal ? globalGroupColumn.value : fileGroupColumns.value[keyFileName]
    if (!col) continue
    const anyInBatch = collection.value.features.some(f => {
      if (!isGlobal && f.properties?.fileName !== keyFileName) return false
      return bucketKeyForValue(f.properties?.[col], col) === keyValue &&
        featuresInUpload.has(f.properties?.local_uuid)
    })
    if (!anyInBatch) continue

    if (choice.kind === 'existing') {
      resolvedTemplateIds[key] = choice.templateId
    } else if (choice.kind === 'new') {
      try {
        const groupFeatures = collection.value.features.filter(f => {
          if (!isGlobal && f.properties?.fileName !== keyFileName) return false
          return bucketKeyForValue(f.properties?.[col], col) === keyValue
        })
        // Use the same lookup features use so template/preview/persisted colors agree.
        const lookupKey = isGlobal
          ? `val|${GLOBAL_PREFIX}${KEY_SEP}${keyValue}`
          : `val|${keyFileName}${KEY_SEP}${keyValue}`
        const color = groupColorLookup.value.get(lookupKey)
          || resolveGroupColor(groupFeatures, key)
        const response = await API({
          method: 'POST',
          url: `${HOST}/project/${projectId}/templates`,
          data: {
            name: choice.name,
            description: null,
            color,
            geometry_type: null,
            shared: false,
            geofence: false,
            geofence_events: null,
            image_required: false,
            data_fields: null,
            project_id: projectId
          }
        })
        const created = response.data
        resolvedTemplateIds[key] = created.id
        store.commit('geo/ADD_REPORT_TEMPLATE', created)
      } catch (e) {
        console.error('Failed to create template:', e)
        toast({ title: `Could not create template "${choice.name}"`, variant: 'destructive' })
      }
    }
  }

  // Fills in the preview's group color when a feature has no explicit color set.
  const resolveFeatureGroupColor = (feature) => {
    const fName = feature.properties?.fileName || 'Unknown'
    const lookup = groupColorLookup.value
    if (globalGroupColumn.value) {
      const col = globalGroupColumn.value
      const bucket = bucketKeyForValue(feature.properties?.[col], col)
      return lookup.get(`val|${GLOBAL_PREFIX}${KEY_SEP}${bucket}`)
    }
    const col = fileGroupColumns.value[fName]
    if (col) {
      const bucket = bucketKeyForValue(feature.properties?.[col], col)
      return lookup.get(`val|${fName}${KEY_SEP}${bucket}`)
    }
    return lookup.get(`file|${fName}`)
  }

  // Client-generated codes let the server echo them back so we can match
  // responses to local features without relying on result ordering.
  const codeToFeature = new Map()

  const buildPayload = (feature) => {
    let tplKey = null
    if (globalGroupColumn.value) {
      const col = globalGroupColumn.value
      tplKey = `${GLOBAL_PREFIX}${KEY_SEP}${bucketKeyForValue(feature.properties?.[col], col)}`
    } else {
      const fName = feature.properties?.fileName
      const col = fileGroupColumns.value[fName]
      tplKey = col ? `${fName}${KEY_SEP}${bucketKeyForValue(feature.properties?.[col], col)}` : null
    }
    const templateId = tplKey ? resolvedTemplateIds[tplKey] : undefined
    const code = uuidv4()
    codeToFeature.set(code, feature)
    return {
      type: 'Feature',
      properties: {
        code,
        name: feature.properties.name || feature.properties.fileName || 'Unnamed',
        description: feature.properties.description || '',
        shared: feature.properties.shared || false,
        is_static: feature.properties.is_static || false,
        member_id: store.getters['user/getMember']?.member_id,
        created_origin: Date.now(),
        type: feature.geometry.type,
        subtype: 'report',
        color: feature.properties.color || resolveFeatureGroupColor(feature) || '#0059ff',
        project_id: feature.properties.project_id || projectId,
        data: [],
        ...(templateId != null ? { template_id: templateId } : {})
      },
      geometry: feature.geometry
    }
  }

  // Chunk client-side so nginx body limits don't reject big imports.
  const CHUNK_SIZE = 500
  const chunks = []
  for (let i = 0; i < featuresToProcess.length; i += CHUNK_SIZE) {
    chunks.push(featuresToProcess.slice(i, i + CHUNK_SIZE))
  }

  let totalUploaded = 0
  const uploadedLocalUuids = new Set()
  const featuresWithResources = []
  let fatalError = null

  uploadProgressRef.value?.setChunkProgress({
    chunkIndex: 0,
    chunkCount: chunks.length,
    uploaded: 0,
    total: featuresToProcess.length
  })

  for (let chunkIdx = 0; chunkIdx < chunks.length; chunkIdx++) {
    if (uploadCancelled.value) break
    const chunk = chunks[chunkIdx]
    uploadProgressRef.value?.setChunkProgress({
      chunkIndex: chunkIdx,
      chunkCount: chunks.length,
      uploaded: totalUploaded,
      total: featuresToProcess.length
    })

    try {
      const payloadFC = {
        type: 'FeatureCollection',
        features: chunk.map(buildPayload)
      }
      const response = await API({
        method: 'POST',
        url: `${HOST}/org/${selectedOrgId.value}/geo/feature/bulk?project_id=${projectId}`,
        data: payloadFC
      })
      const created = response.data?.created || []
      for (const row of created) {
        const feature = codeToFeature.get(row.code)
        if (!feature) continue
        uploadedLocalUuids.add(feature.properties.local_uuid)
        if (feature.properties.resource) {
          featuresWithResources.push({ resource: feature.properties.resource, code: row.code })
        }
      }
      totalUploaded += created.length
      uploadProgressRef.value?.setChunkProgress({
        chunkIndex: chunkIdx,
        chunkCount: chunks.length,
        uploaded: totalUploaded,
        total: featuresToProcess.length
      })
    } catch (error) {
      console.error('Bulk upload failed:', error)
      fatalError = error
      break
    }
  }

  collection.value.features = collection.value.features.filter(f => {
    const uuid = f.properties?.local_uuid
    if (!uuid) return true
    if (uploadedLocalUuids.has(uuid)) return false
    return true
  })
  // Drop MultiPolygon parents whose split children have all been uploaded.
  collection.value.features = collection.value.features.filter(f => {
    if (f.geometry?.type !== 'MultiPolygon') return true
    const parentUuid = f.properties?.local_uuid
    if (!parentUuid) return true
    const splitsRemain = featuresToProcess.some(sf =>
      sf.properties?._parentUuid === parentUuid &&
      !uploadedLocalUuids.has(sf.properties.local_uuid)
    )
    const anyUploaded = featuresToProcess.some(sf =>
      sf.properties?._parentUuid === parentUuid &&
      uploadedLocalUuids.has(sf.properties.local_uuid)
    )
    return !(anyUploaded && !splitsRemain)
  })

  // Resource uploads need the server-issued code, so they run after feature inserts commit.
  if (featuresWithResources.length > 0) {
    uploadProgressRef.value?.setResourceProgress(featuresWithResources.length)
    await Promise.allSettled(
      featuresWithResources.map(({ resource, code }) =>
        uploadResource(resource, code).catch(e => console.error('Resource upload failed:', e))
      )
    )
  }

  syncMapSource()

  isUploading.value = false
  selectedFeatures.value = []

  if (fatalError) {
    const msg = fatalError?.response?.data?.detail || fatalError?.message || 'Upload failed'
    toast({ title: 'Upload failed', description: msg, variant: 'destructive' })
  } else if (!uploadCancelled.value) {
    toast({ title: `Uploaded ${totalUploaded} features` })
  }
}

const handleUploadCancel = () => {
  uploadCancelled.value = true
  isUploading.value = false
  showUploadProgressDialog.value = false

  toast({
    title: 'Upload stopped',
    description: 'The upload process has been cancelled'
  })
}

const handleValidateFeature = (feature) => {
  syncMapSource()
}

const handleUpdateFeature = (feature) => {
  const index = collection.value.features.findIndex(f => f.properties.local_uuid === feature.properties.local_uuid)
  if (index !== -1) {
    collection.value.features[index] = feature
  }
  syncMapSource()
}

const createFeature = async (feature) => {
  try {
    const result = await store.dispatch('geo/createFeature', {
      geojson: feature,
      project_id: feature.properties.project_id
    })

    router.push({
      name: 'Feature',
      params: {
        org_id: feature.properties.organization_id,
        project_id: feature.properties.project_id,
        featureCode: feature.properties.code
      }
    })
  } catch (error) {
  }
}

const fetchOrgProjects = async () => {
  if (!selectedOrgId.value) return
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${selectedOrgId.value}/project`
    })
    if (response.data && Array.isArray(response.data)) {
      orgProjects.value = response.data
    }
  } catch (error) {
    console.error('Failed to fetch org projects:', error)
  }
}

watch(selectedProjectId, (id) => {
  if (id) store.dispatch('geo/fetchProjectReportTemplates', id)
}, { immediate: true })

const switchProject = (val) => {
  const newId = val ? parseInt(val) : null
  if (!newId || newId === selectedProjectId.value) return
  router.push({
    name: 'ProjectImport',
    params: {
      org_id: selectedOrgId.value,
      project_id: newId
    }
  })
}

onMounted(async () => {
  if (selectedOrgId.value) {
    store.dispatch('geo/fetchAllTemplates', selectedOrgId.value)
    await fetchOrgProjects()
  }
})

watch(selectedOrgId, async (newOrgId) => {
  if (newOrgId) {
    store.dispatch('geo/fetchAllTemplates', newOrgId)
    await fetchOrgProjects()

    // selectedProjectId is route-derived; "reset" means navigating off the project route.
    if (selectedProjectId.value && projects.value) {
      const projectExists = projects.value.some(p => p.project_id === selectedProjectId.value)
      if (!projectExists) {
        router.replace({
          name: 'FileViewer',
          params: { org_id: newOrgId }
        })
      }
    }
  }
})

watchEffect(async () => {
  await handleFiles(loadedFiles.value)
})

onBeforeUnmount(() => {
  reset()
})
</script>

<template>
  <div class="bg-background h-full flex flex-col">
    <div v-if="requireProjectSelection" class="flex-1 p-4 min-h-0 flex items-center justify-center">
      <div class="w-full max-w-md text-center space-y-4">
        <h3 class="text-lg font-semibold">Choose a project to import into</h3>
        <p class="text-sm text-muted-foreground">
          Imported features and any new templates are scoped to a single project.
        </p>
        <ProjectSelect
          :projects="projects"
          :model-value="selectedProjectId?.toString() ?? ''"
          placeholder="Select a project"
          @update:model-value="switchProject"
        />
      </div>
    </div>

    <div v-else-if="collection.features.length > 0" class="p-4">
      <div class="mb-4 max-w-sm">
        <ProjectSelect
          :projects="projects"
          :model-value="selectedProjectId?.toString() ?? ''"
          placeholder="Select a project"
          @update:model-value="switchProject"
        />
      </div>

      <BulkUploadTable
        :file-groups="fileGroups"
        :is-uploading="isUploading"
        :selected-project="selectedProjectId ? projects.find(p => p.project_id === selectedProjectId) : currentProject"
        :merge-select-mode="mergeSelectMode"
        :all-templates="allTemplates"
        :global-group-column="globalGroupColumn"
        :global-available-columns="globalAvailableColumns"
        @set-value-group-template="(fileName, value, choice) => setValueGroupTemplate(fileName, value, choice)"
        @toggle-merge-select-mode="toggleMergeSelectMode"
        @toggle-merge-selection="toggleMergeSelection"
        @confirm-merge-files="confirmMergeFiles"
        @split-merged-file="splitMergedFile"
        @set-file-group-column="(fileName, column) => setFileGroupColumn(fileName, column)"
        @toggle-value-visibility="(fileName, value) => toggleValueGroupVisibility(fileName, value)"
        @set-global-group-column="setGlobalGroupColumn"
        @toggle-global-value-visibility="toggleGlobalValueVisibility"
        @set-global-value-template="(value, choice) => setValueGroupTemplate(GLOBAL_PREFIX, value, choice)"
        @select-features="handleSelectFeatures"
        @preview-feature="handlePreviewFeature"
        @edit-feature="handleEditFeature"
        @delete-feature="handleDeleteFeature"
        @bulk-edit="handleBulkEdit"
        @upload-selected="handleUploadSelected"
        @validate-feature="handleValidateFeature"
        @feature-uploaded="handleFeatureUploaded"
        @update-feature="handleUpdateFeature"
        @toggle-file-visibility="toggleFileVisibility"
        @toggle-file-group-polygons="toggleFileGroupPolygons"
        @zoom-to-file="zoomToFile"
        @show-metadata="openMetadataDialog"
        @show-feature-metadata="openFeatureMetadataDialog"
      />
    </div>
    <div v-else class="flex-1 p-4 min-h-0">
      <input
        ref="fileInputRef"
        type="file"
        multiple
        class="hidden"
        :accept="FILE_INPUT_ACCEPT"
        @change="onFilePicked"
      />
      <div
        class="h-full border-2 border-dashed rounded-lg p-8 transition-all duration-200 cursor-pointer flex items-center justify-center"
        :class="emptyStateDragging
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-muted-foreground'"
        @click="fileInputRef?.click()"
        @dragenter.prevent="emptyStateDragging = true"
        @dragover.prevent
        @dragleave.prevent="(e) => { if (e.target === e.currentTarget) emptyStateDragging = false }"
        @drop.prevent="onEmptyStateDrop"
      >
        <div class="flex flex-col items-center justify-center text-center pointer-events-none">
          <Upload class="w-12 h-12 mb-4 text-muted-foreground" />
          <p class="text-lg font-medium mb-2">Drop files here or click to browse</p>
          <p class="text-xs text-muted-foreground max-w-md">
            KML, KMZ, GeoJSON, GPX, FIT, Shapefile (.shp + sidecars or .zip), and images with GPS data
          </p>
        </div>
      </div>
    </div>

    <BulkEditDialog
      :open="showBulkEditDialog"
      :features="selectedFeatures"
      :projects="projects"
      @close="showBulkEditDialog = false"
      @apply="handleBulkEditApply"
    />

    <FileMetadataDialog
      :open="metadataDialog.open"
      :file-name="metadataDialog.fileName"
      :features="metadataDialogFeatures"
      @close="metadataDialog = { open: false, fileName: '', singleFeature: null }"
    />

    <ProjWarningDialog
      :open="projWarning.open"
      :file-name="projWarning.fileName"
      @confirm="onProjWarningConfirm"
      @cancel="onProjWarningCancel"
    />

    <UploadProgressDialog
      ref="uploadProgressRef"
      :open="showUploadProgressDialog"
      :features="selectedFeatures.length > 0 ? selectedFeatures : collection.features"
      :is-uploading="isUploading"
      @close="showUploadProgressDialog = false"
      @cancel="handleUploadCancel"
    />
  </div>
</template>
